import type { ISchedulerData, ISchedulerTask } from '@interfaces/IScheduler';
import type {
  IMasterScheduleMilestone,
  IMasterScheduleModel,
  IMasterScheduleRow,
  MasterScheduleStatusKind,
} from '@interfaces/IMasterSchedule';
import { expectedTaskProgress } from './projectProgress.derive';
import { groupInto } from '../../SchedulerPage/lib/groupInto';

/* Pure derivation for the Master Schedule / Critical Path Gantt card.
   No React, no fetching, no Date.now() — `todayMs` is injected (JST
   calendar date parsed as UTC midnight, same convention as
   projectProgress.derive.ts) so everything is unit-testable. */

const DAY_MS = 86_400_000;

/** UTC midnight of the first day of the month containing `ms`. */
function monthStartUtc(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
}

/** UTC midnight of the first day `n` months after the month start `ms`.
 *  NOTE: date-fns `startOfMonth`/`addMonths` operate in LOCAL time, not UTC,
 *  which would reintroduce timezone drift for users outside UTC. date-fns-tz
 *  is not a project dependency, so we keep these UTC helpers intentionally. */
function addMonthsUtc(monthStartMs: number, n: number): number {
  const d = new Date(monthStartMs);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1);
}

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

/** Status from the rounded actual-vs-planned rollups, in the spirit of
 *  projectProgress: any rounded shortfall against plan = delayed (the
 *  progress card likewise flags every non-zero negative variance as
 *  "behind plan"); nothing started AND nothing due yet = pending;
 *  everything else = on track. */
export function rowStatus(progressPct: number, plannedPct: number): MasterScheduleStatusKind {
  if (progressPct <= 0 && plannedPct <= 0) return 'pending';
  if (progressPct < plannedPct) return 'delayed';
  return 'on-track';
}

/** A task's planned window in epoch ms, or null when unparseable. */
function taskSpan(task: ISchedulerTask): { start: number; end: number } | null {
  const start = Date.parse(task.start);
  const end = Date.parse(task.end);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return { start, end };
}

/* Synthetic parent keys so group roots and inner children share one map
   without id collisions (mirrors rowGeometry.ts buildRowsLayout):
     roots of group g   → key `g:<groupId>` (parentId == null)
     children of task t  → key `t:<taskId>` */
const ROOT_PREFIX = 'g:';
const CHILD_PREFIX = 't:';

/** Accumulator for the post-order rollup of a summary node (group or summary
 *  task) over its descendant LEAVES. The bar span covers only NON-milestone
 *  leaves; progress/planned means cover ALL leaves (milestones included), the
 *  summary tasks' own `progressPercent` excluded — same basis as foldLeaf in
 *  rowGeometry.ts. `hasMilestone` bubbles a milestone anywhere in the subtree
 *  so a milestone-only branch isn't dropped as "empty". */
interface IRoll {
  barStart?: number;
  barEnd?: number;
  progressSum: number;
  plannedSum: number;
  leafCount: number;
  hasMilestone: boolean;
}

function newRoll(): IRoll {
  return { progressSum: 0, plannedSum: 0, leafCount: 0, hasMilestone: false };
}

function extendBar(roll: IRoll, span: { start: number; end: number }): void {
  roll.barStart = roll.barStart === undefined ? span.start : Math.min(roll.barStart, span.start);
  roll.barEnd = roll.barEnd === undefined ? span.end : Math.max(roll.barEnd, span.end);
}

function mergeRoll(parent: IRoll, child: IRoll): void {
  if (child.barStart !== undefined && child.barEnd !== undefined) {
    extendBar(parent, { start: child.barStart, end: child.barEnd });
  }
  parent.progressSum += child.progressSum;
  parent.plannedSum += child.plannedSum;
  parent.leafCount += child.leafCount;
  if (child.hasMilestone) parent.hasMilestone = true;
}

/** Derive the full Gantt model from a schedule. Returns null when no task
 *  carries a parseable date span (nothing can be positioned on a timeline).
 *  Row order = schedule order. Rows are hierarchical: one row per group AND
 *  one per summary task (any task with children), each carrying a rolled-up
 *  bar span (min start / max end of its descendant non-milestone leaves) and
 *  rolled-up progress/planned means over its descendant leaves. Leaf tasks
 *  roll up into their nearest summary ancestor — they get no row of their own.
 *  A flat group (only top-level leaves) collapses to exactly one depth-0 row,
 *  identical to the pre-hierarchy output.
 *
 *  Milestone tasks (`isMilestone`, the Scheduler's discriminator) become
 *  diamond points on their parent summary/group row instead of stretching the
 *  bar span — a milestone-only row renders diamonds with no bar (null fracs).
 *  They still extend the timeline window and stay in the progress rollups. */
export function deriveMasterSchedule(data: ISchedulerData, todayMs: number): IMasterScheduleModel | null {
  // Window pass — every datable task (milestones included) sets the bounds.
  let minStart = Infinity;
  let maxEnd = -Infinity;
  for (const task of data.tasks) {
    const span = taskSpan(task);
    if (!span) continue;
    minStart = Math.min(minStart, span.start);
    maxEnd = Math.max(maxEnd, span.end);
  }
  if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd) || maxEnd < minStart) return null;

  // Timeline window clamped to whole months: month start of the earliest
  // task start through the month start AFTER the latest task end (exclusive).
  const windowStartMs = monthStartUtc(minStart);
  const windowEndMs = addMonthsUtc(monthStartUtc(maxEnd), 1);
  const spanMs = windowEndMs - windowStartMs;

  const monthStartsMs: number[] = [];
  for (let m = windowStartMs; m < windowEndMs; m = addMonthsUtc(m, 1)) monthStartsMs.push(m);

  // Index the whole forest once (mirror rowGeometry.ts buildRowsLayout).
  const childrenByParent = groupInto(data.tasks, (task) =>
    task.parentId == null ? `${ROOT_PREFIX}${task.groupId}` : `${CHILD_PREFIX}${task.parentId}`,
  );
  const hasChildren = (id: string): boolean => childrenByParent.has(`${CHILD_PREFIX}${id}`);

  // Pass 1 — post-order rollup over descendant leaves into one IRoll per
  // summary node (group root key or summary-task key). Recursion bottoms out
  // at leaves: a leaf folds its progress/planned into the parent and (unless a
  // milestone) extends the bar span.
  const rollByKey = new Map<string, IRoll>();
  // `visiting` guards against a cyclic `parentId` chain in upstream data (DAITEC
  // is mocked/unvalidated in Phase 1, R-01): a cycle would otherwise recurse
  // until the stack overflows. A revisited node contributes an empty rollup.
  const computeRoll = (nodeKey: string, visiting: Set<string> = new Set()): IRoll => {
    const roll = newRoll();
    if (visiting.has(nodeKey)) return roll;
    visiting.add(nodeKey);
    for (const child of childrenByParent.get(nodeKey) ?? []) {
      if (hasChildren(child.id)) {
        mergeRoll(roll, computeRoll(`${CHILD_PREFIX}${child.id}`, visiting));
        continue;
      }
      roll.progressSum += child.progressPercent;
      roll.plannedSum += expectedTaskProgress(child, todayMs);
      roll.leafCount += 1;
      if (child.isMilestone) {
        roll.hasMilestone = true;
      } else {
        const span = taskSpan(child);
        if (span) extendBar(roll, span);
      }
    }
    rollByKey.set(nodeKey, roll);
    return roll;
  };
  for (const group of data.groups) computeRoll(`${ROOT_PREFIX}${group.id}`);

  // Direct milestone children of a node → diamond points on its own row.
  const directMilestones = (nodeKey: string): IMasterScheduleMilestone[] => {
    const out: IMasterScheduleMilestone[] = [];
    for (const child of childrenByParent.get(nodeKey) ?? []) {
      if (!child.isMilestone) continue;
      const span = taskSpan(child);
      if (!span) continue;
      out.push({
        key: child.id,
        name: child.name,
        // Diamond centered on the midpoint of its (inclusive) calendar day.
        frac: clamp01((span.start + DAY_MS / 2 - windowStartMs) / spanMs),
        // Raw ISO/YYYY-MM-DD — formatLongDate anchors it in JST (no off-by-one).
        dateIso: child.start,
      });
    }
    return out;
  };

  // ms (UTC midnight) → 'YYYY-MM-DD'. barStart comes from Date.parse of a task's
  // 'YYYY-MM-DD' start, so it's UTC midnight and slicing the ISO string is exact.
  const isoDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

  const makeRow = (
    nodeKey: string,
    key: string,
    name: string,
    depth: number,
    /** Scheduler task id for summary rows; null for group rows (not tasks). */
    taskId: string | null,
  ): IMasterScheduleRow => {
    const roll = rollByKey.get(nodeKey) ?? newRoll();
    const progressPct = roll.leafCount === 0 ? 0 : Math.round(roll.progressSum / roll.leafCount);
    const plannedPct = roll.leafCount === 0 ? 0 : Math.round(roll.plannedSum / roll.leafCount);
    const hasBar = roll.barStart !== undefined && roll.barEnd !== undefined;
    return {
      key,
      name,
      taskId,
      startIso: hasBar ? isoDate(roll.barStart!) : null,
      startFrac: hasBar ? clamp01((roll.barStart! - windowStartMs) / spanMs) : null,
      // `end` is an inclusive calendar date: the bar covers through that day.
      endFrac: hasBar ? clamp01((roll.barEnd! + DAY_MS - windowStartMs) / spanMs) : null,
      milestones: directMilestones(nodeKey),
      progressPct,
      plannedPct,
      variancePct: progressPct - plannedPct,
      statusKind: rowStatus(progressPct, plannedPct),
      depth,
    };
  };

  // Pass 2 — pre-order emit. Group rows at depth 0, then a summary row per
  // child-bearing task (depth+1 per nesting level). Leaf children roll up and
  // get no row; their milestone-ness lands on their parent's direct milestones.
  const rows: IMasterScheduleRow[] = [];
  const emitChildren = (parentKey: string, depth: number): void => {
    for (const child of childrenByParent.get(parentKey) ?? []) {
      if (!hasChildren(child.id)) continue; // leaves roll up — no row
      rows.push(makeRow(`${CHILD_PREFIX}${child.id}`, child.id, child.name, depth, child.id));
      emitChildren(`${CHILD_PREFIX}${child.id}`, depth + 1);
    }
  };
  for (const group of data.groups) {
    const rootKey = `${ROOT_PREFIX}${group.id}`;
    const roll = rollByKey.get(rootKey);
    // Drop empty groups: no bar to draw AND no milestone anywhere beneath.
    if (!roll || (roll.barStart === undefined && !roll.hasMilestone)) continue;
    rows.push(makeRow(rootKey, group.id, group.name, 0, null));
    emitChildren(rootKey, 1);
  }

  const todayFrac =
    todayMs >= windowStartMs && todayMs < windowEndMs ? (todayMs - windowStartMs) / spanMs : null;

  return { windowStartMs, windowEndMs, monthStartsMs, windowDays: spanMs / DAY_MS, todayFrac, rows };
}
